'use client';

import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { motion } from 'framer-motion';

const loaderVariants = cva('flex items-center justify-center', {
  variants: {
    variant: {
      spinner: 'relative',
      dots: 'flex space-x-1',
      pulse: 'flex space-x-2',
      bars: 'flex space-x-1',
      ripple: 'relative',
      wave: 'flex space-x-1',
    },
  },
  defaultVariants: {
    variant: 'spinner',
  },
});

const spinnerSize = cva('', {
  variants: {
    size: {
      sm: 'h-6 w-6',
      md: 'h-8 w-8',
      lg: 'h-12 w-12',
      xl: 'h-16 w-16 border-5',
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

const dotsSize = cva('', {
  variants: {
    size: {
      sm: 'h-2 w-2',
      md: 'h-3 w-3',
      lg: 'h-4 w-4',
      xl: 'h-6 w-6',
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

const barsSize = cva('', {
  variants: {
    size: {
      sm: 'w-1 h-4',
      md: 'w-1.5 h-6',
      lg: 'w-2 h-8',
      xl: 'w-3 h-12',
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

export interface LoaderProps extends VariantProps<typeof loaderVariants> {
  className?: string;
  text?: string;
  fullScreen?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export function Loader({
  variant = 'spinner',
  size = 'sm',
  className,
  text,
  fullScreen = false,
}: LoaderProps) {
  const containerClasses = cn(
    'flex flex-col items-center justify-center',
    fullScreen && 'fixed inset-0 bg-background/80 backdrop-blur-sm z-50',
    className,
  );

  const renderLoader = () => {
    switch (variant) {
      case 'spinner':
        return (
          <motion.div
            className={cn(
              'border-3 border-gray-50 border-t-blue-900 rounded-full',
              spinnerSize({ size }),
            )}
            animate={{ rotate: [0, 360] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'linear',
              times: [0, 1],
            }}
          />
        );

      case 'dots':
        return (
          <div className="flex space-x-1">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className={cn('bg-blue-900 rounded-full', dotsSize({ size }))}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 1.4,
                  repeat: Infinity,
                  delay: i * 0.2,
                }}
              />
            ))}
          </div>
        );

      case 'pulse':
        return (
          <div className="flex space-x-2">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className={cn('bg-blue-900 rounded-full', dotsSize({ size }))}
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.3, 1, 0.3],
                }}
                transition={{
                  duration: 1.2,
                  repeat: Infinity,
                  delay: i * 0.15,
                }}
              />
            ))}
          </div>
        );

      case 'bars':
        return (
          <div className="flex space-x-1">
            {[0, 1, 2, 3].map((i) => (
              <motion.div
                key={i}
                className={cn('bg-blue-900 rounded-xs', barsSize({ size }))}
                animate={{
                  scaleY: [1, 2, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 1.2,
                  repeat: Infinity,
                  delay: i * 0.1,
                }}
              />
            ))}
          </div>
        );

      case 'ripple':
        return (
          <div className="relative">
            <motion.div
              className={cn('border-3 border-blue-900 rounded-full inset-0', spinnerSize({ size }))}
              animate={{
                scale: [1, 1.5, 1],
                opacity: [1, 0, 1],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          </div>
        );

      case 'wave':
        return (
          <div className="flex space-x-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <motion.div
                key={i}
                className={cn('bg-blue-900 rounded-full', dotsSize({ size }))}
                animate={{
                  scaleY: [1, 1.5, 1],
                  opacity: [0.3, 1, 0.3],
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  delay: i * 0.1,
                }}
              />
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={containerClasses}>
      <div className={cn(loaderVariants({ variant }))}>{renderLoader()}</div>
      {text && (
        <motion.p
          className="mt-4 text-sm text-muted-foreground font-medium"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {text}
        </motion.p>
      )}
    </div>
  );
}

export function FullScreenLoader({ text = 'Cargando...' }: { text?: string }) {
  return <Loader variant="spinner" size="xl" text={text} fullScreen />;
}

export function OverlayLoader({ text }: { text?: string }) {
  return (
    <div className="absolute inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center z-40">
      <Loader variant="dots" size="lg" text={text} />
    </div>
  );
}
